import gsap from 'gsap';
import { MutableRefObject } from 'react';

import useAudioRoutingStore from 'state/audioRoutingStore';
import { useAudioVisualizerStore } from 'state/audioVisualizerStore';
import { useCloudOverlayStore } from 'state/cloudOverlayStore';
import { CUSTOM_CAMERA_VARIANT } from 'state/customCameraStore';
import { useAudioStore } from 'state/sfx/audioStore';
import { useMusicStore } from 'state/sfx/musicStore';
import { useThreeStore } from 'state/threeStore';
import { useVoiceAssistantStore } from 'state/voiceAssistantStore';

import { CustomCameraOperator, SPACE_VIEW_CAMERA_NEAR } from './CustomCamera';
import SETTINGS from './CustomCamera.settings';

const EARTH_SURFACE_RADIUS = -994;

export const introAnimation = (
  operator: MutableRefObject<CustomCameraOperator>
) => {
  const timeline = gsap.timeline();
  timeline
    .call(() => {
      useThreeStore
        .getState()
        .setEarth({ stage: 'DESTINATION_TRANSITION_START' });
      timeline.pause();

      const unsubscribe = useThreeStore.subscribe(stage => {
        if (stage.earth.stage === 'DESTINATION_TRANSITION_END') {
          timeline.resume();
          unsubscribe();
        }
      });
    })
    .to(operator.current, {
      radius: EARTH_SURFACE_RADIUS,
      inclination: Math.PI / 4,
      duration: 1,
    })
    .call(
      () => {
        useThreeStore.getState().setCloudsTransition({ stage: 'INTRO' });
        useAudioStore.getState().fadeIn('general_clouds_long');
      },
      [],
      0.7
    )
    .call(
      () => {
        operator.current.radius = -990;
        operator.current.near = 1;
      },
      [],
      0.74
    )
    .call(() => {
      const { setTiles, setEarth } = useThreeStore.getState();
      setTiles({ isRendered: true, stage: 'VISIBLE' });
      setEarth({ isRendered: false, stage: 'HIDDEN' });
    })
    .to(operator.current, {
      radius: -80,
      duration: 5,
      ease: 'power4.out',
    })
    .call(
      () => {
        useAudioVisualizerStore.getState().setVisible(true);
        useAudioVisualizerStore.getState().setPosition('CENTER');

        useMusicStore.getState().fadeIn('introAudio');
        useAudioStore.getState().fadeIn('atmos');
      },
      [],
      '>-1'
    )
    .call(() => {
      useAudioRoutingStore.getState().setIsPlaying(true);
      useVoiceAssistantStore
        .getState()
        .setVoiceAssistantSettings({ isVoiceAssistantVisible: true });
      useCloudOverlayStore.getState().setVisible(true);
    })
    .call(
      () => {
        useAudioVisualizerStore.getState().setPosition('BOTTOM');
      },
      [],
      '+=1'
    )
    .call(
      () => {
        useThreeStore
          .getState()
          .setIndicationRings({ isRendered: true, stage: 'VISIBLE' });
      },
      [],
      '>0'
    );
};

export const outroAnimation = (
  operator: MutableRefObject<CustomCameraOperator>
) =>
  gsap
    .timeline()
    .call(() => {
      useThreeStore.getState().setCloudsTransition({ stage: 'OUTRO' });
      useAudioStore.getState().fadeIn('general_clouds_long');
    })
    .to(operator.current, {
      radius: EARTH_SURFACE_RADIUS,
      inclination: Math.PI / 4,
      duration: 3,
    })
    .call(
      () => {
        const { setTiles, setEarth, setSky } = useThreeStore.getState();
        setTiles({ isRendered: false, stage: 'HIDDEN' });
        setEarth({ isRendered: true, stage: 'OUTRO_TRANSITION_START' });
        operator.current.near = SPACE_VIEW_CAMERA_NEAR;
        setSky('NIGHT');
      },
      [],
      2.7
    )
    .to(operator.current, {
      radius: window.innerWidth > window.innerHeight ? -1001.5 : -1003.5,
      inclination: Math.PI / 4,
      duration: 1,
    })
    .call(() => {
      useThreeStore.getState().setEarth({ stage: 'OUTRO_TRANSITION_END' });
    });

export const animation = (
  operator: MutableRefObject<CustomCameraOperator>,
  view: CUSTOM_CAMERA_VARIANT
) => {
  const settings = SETTINGS[view];
  if (!settings) return;

  const { radius, inclination, azimuth } = settings;

  const timeline = gsap.timeline();
  timeline.to(operator.current, { radius, inclination, duration: 2 }, 'start');

  if (azimuth) {
    timeline.to(operator.current, { azimuth, duration: 2 }, 'start');
  }
};
